import React, { useRef, useState, useEffect } from "react";
import { toast } from "react-hot-toast";

export default function ExcelAssessment({ onComplete }) {
  const [screenStream, setScreenStream] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [errorStatus, setErrorStatus] = useState("");
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);

  // Stop recording thoroughly
  const stopRecordingAndStream = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    if (screenStream) {
      screenStream.getTracks().forEach((track) => track.stop());
    }
  };

  useEffect(() => {
    return () => {
      stopRecordingAndStream();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenStream]);

  const startScreenRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { displaySurface: "monitor" },
        audio: true, // Attempt to capture system/mic audio if supported
      });
      setScreenStream(stream);

      // Handle user manually stopping from browser bar
      stream.getVideoTracks()[0].onended = () => {
        toast.error("Screen sharing was stopped. You must share screen to complete the test.");
        stopRecordingAndStream();
        setScreenStream(null);
      };

      // 🚀 Robust MIME Check for Screen Recording
      let mimeType = "video/webm;codecs=vp8,opus";
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = "video/mp4"; // iOS/Safari Fallback
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = "video/quicktime"; 
        }
      }
      
      const mediaRecorder = new MediaRecorder(stream, { 
        mimeType,
        videoBitsPerSecond: 150000 
      });
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          recordedChunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const videoBlob = new Blob(recordedChunksRef.current, { type: "video/webm" });
        console.log("Excel screen recording completed, size:", videoBlob.size);
        
        // When stopped naturally by our finish button, we trigger onComplete here?
        // Wait, onComplete should be triggered inside handleFinishTest so we have access to the worksheet data.
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(1000);
      setIsRecording(true);
      toast.success("Screen recording started! You can begin the test.");
      
    } catch (err) {
      console.error("Screen recording error:", err);
      setErrorStatus("Failed to start screen recording. Please grant permissions.");
    }
  };

  const handleFinishTest = async () => {
    // Stop recorder to finish the blob
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    if (screenStream) {
      screenStream.getTracks().forEach((t) => t.stop());
    }

    // Wait a tiny moment for MediaRecorder to fire its final ondataavailable event
    await new Promise((resolve) => setTimeout(resolve, 500));

    const finalVideoBlob = new Blob(recordedChunksRef.current, { type: "video/webm" });
    
    // We no longer have spreadsheet data
    onComplete(null, finalVideoBlob);
  };

  if (!screenStream) {
    return (
      <div className="interview-container excel-assessment-intro" style={{ textAlign: "center", padding: "50px", color: "white" }}>
        <h2>📊 Final Stage: Excel Practical Test</h2>
        <p style={{ marginTop: "20px", fontSize: "1.1rem", maxWidth: "600px", margin: "20px auto", color: "#ccc" }}>
          You will now enter a realistic Microsoft Excel environment to perform a practical data task.
          Since this is a practical exam, <strong>you must share your entire screen</strong> so we can record your work.
        </p>
        {errorStatus && <p style={{ color: "#ef4444", marginBottom: "15px" }}>{errorStatus}</p>}
        <button 
          onClick={startScreenRecording}
          style={{
            padding: "12px 24px",
            fontSize: "1.2rem",
            backgroundColor: "#2563eb",
            color: "white",
            border: "none",
            borderRadius: "8px",
            cursor: "pointer",
            fontWeight: "bold",
            marginTop: "10px",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
          }}
        >
          Share Screen & Start Test
        </button>
      </div>
    );
  }

  return (
    <div style={{ width: "100%", height: "100vh", display: "flex", flexDirection: "column", backgroundColor: "#0f172a" }}>
      <div style={{ 
        padding: "15px 30px", 
        backgroundColor: "#1e293b", 
        color: "white", 
        display: "flex", 
        justifyContent: "space-between",
        alignItems: "center",
        borderBottom: "1px solid #334155",
        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)"
      }}>
        <div>
          <h3 style={{ margin: 0, fontSize: "1.4rem" }}>Excel Practical Assessment</h3>
          <span style={{ fontSize: "0.9rem", color: "#ef4444", display: "flex", alignItems: "center", gap: "8px", marginTop: "4px" }}>
            <div style={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: "#ef4444", animation: "blink 1.5s infinite" }}></div>
            🔴 Recording your screen...
          </span>
        </div>
        <button 
          onClick={handleFinishTest}
          style={{
            padding: "12px 24px",
            backgroundColor: "#10b981",
            color: "white",
            border: "none",
            borderRadius: "8px",
            cursor: "pointer",
            fontWeight: "bold",
            fontSize: "1.1rem",
            transition: "all 0.2s"
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = "#059669"}
          onMouseOut={(e) => e.target.style.backgroundColor = "#10b981"}
        >
          Finish & Submit Recording
        </button>
      </div>
      
      <div style={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center", padding: "40px" }}>
        <div style={{ 
          maxWidth: "800px", 
          width: "100%", 
          backgroundColor: "#1e293b", 
          borderRadius: "16px", 
          padding: "40px", 
          textAlign: "center",
          border: "1px solid #334155",
          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)"
        }}>
          <div style={{ fontSize: "4rem", marginBottom: "20px" }}>📽️</div>
          <h2 style={{ color: "white", fontSize: "2rem", marginBottom: "20px" }}>Practical Task Instructions</h2>
          <div style={{ textAlign: "left", color: "#cbd5e1", fontSize: "1.2rem", lineHeight: "1.6" }}>
            <p style={{ marginBottom: "15px", color: "#60a5fa", fontWeight: "bold" }}>
              Please perform the following steps:
            </p>
            <ol style={{ paddingLeft: "25px" }}>
              <li style={{ marginBottom: "10px" }}>Open <strong>Microsoft Excel</strong> on your computer.</li>
              <li style={{ marginBottom: "10px" }}>Create a new spreadsheet and perform the requested task.</li>
              <li style={{ marginBottom: "10px" }}>Once you are finished, save your work locally (optional).</li>
              <li style={{ marginBottom: "10px" }}>Click the <strong>"Finish & Submit Recording"</strong> button above to end the test.</li>
            </ol>
            <p style={{ marginTop: "30px", padding: "15px", backgroundColor: "#334155", borderRadius: "8px", fontSize: "1rem", borderLeft: "4px solid #f59e0b" }}>
              <strong>Note:</strong> We are recording your entire screen to evaluate your proficiency in using Excel features and formulas.
            </p>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes blink {
          0% { opacity: 1; }
          50% { opacity: 0.4; }
          100% { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
